import { Anchor, Avatar, Badge, Button, Group, Stack, Table, Text } from '@mantine/core';
import { useState } from 'react';
import { Link } from 'react-router';

import { formatDateTime } from '../../shared/format';
import { CursorPager, PageFallback, hasItems, useCursorPager } from '../../shared/ui';
import { usePracticeRoster } from './queries';

/** Статус записи — как его пишет бэкенд (`RosterBookingDto.status`); подписи — для человека. */
const BOOKING_STATUS_LABEL: Record<string, { label: string; color: string }> = {
  confirmed: { label: 'Подтверждена', color: 'green' },
  cancelled: { label: 'Снята отменой практики', color: 'gray' },
};

/**
 * Роспись практики (CAP-6): кто держит место и когда записался. Какие записи в ней — решает
 * API по статусу практики (Story 8.4 бэкенда, D-21: админская роспись ведёт себя как
 * учительская): на запланированной — подтверждённые, на отменённой — те, что отмена сняла,
 * потому что это люди, которым нужно сообщить. Здесь ничего не фильтруется. Telegram-идентичность
 * (решение Q1) не лежит в таблице открыто: по AD-A12 она раскрывается явным нажатием, по строке.
 */
export function PracticeRoster({
  practiceId,
  practiceStatus,
}: {
  practiceId: string;
  practiceStatus: string;
}) {
  const pager = useCursorPager(practiceId);
  const page = usePracticeRoster(practiceId, pager.cursor);

  return (
    <Stack gap="xs">
      {practiceStatus === 'cancelled' ? (
        <Text size="sm" c="dimmed">
          Практика отменена: ниже — записи, которые сняла отмена. Этим людям нужно сообщить.
        </Text>
      ) : null}
      <PageFallback query={page} empty="Записавшихся нет." loaderSize="sm" />
      {hasItems(page) ? (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ученик</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Telegram</Table.Th>
              <Table.Th>Записался</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {page.data?.items.map((booking) => (
              <Table.Tr key={booking.id}>
                <Table.Td>
                  <Group gap="xs">
                    <Avatar src={booking.student.avatarUrl} radius="xl" size="sm" />
                    <Anchor component={Link} to={`/users/${booking.student.userId}`} size="sm">
                      {booking.student.displayName ?? 'Без имени'}
                    </Anchor>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge
                    variant="light"
                    color={BOOKING_STATUS_LABEL[booking.status]?.color ?? 'gray'}
                  >
                    {BOOKING_STATUS_LABEL[booking.status]?.label ?? booking.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <TelegramIdentity userId={booking.student.telegramUserId} />
                </Table.Td>
                <Table.Td>{formatDateTime(booking.createdAt)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : null}
      <CursorPager query={page} pager={pager} />
    </Stack>
  );
}

function TelegramIdentity({ userId }: { userId: string | null }) {
  const [shown, setShown] = useState(false);

  if (userId === null) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  if (!shown) {
    return (
      <Button variant="subtle" size="compact-xs" onClick={() => setShown(true)}>
        Показать
      </Button>
    );
  }
  return (
    <Anchor href={`tg://user?id=${userId}`} size="sm">
      {userId}
    </Anchor>
  );
}
